import { useState } from "react";
import TechHero from "../components/TechHero";
import ServiceModal from "../components/ServiceModal";
import ParallaxSection from "../components/ParallaxSection";

const serviceIcons = {
  Website: "🌐",
  "Web App": "💻",
  "Mobile App": "📱",
  HRMS: "👥",
  CRM: "📊",
  Automobile: "🚗",
  Banking: "🏦",
  Travel: "🚕",
};

const serviceImages = {
  Website: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&h=600&fit=crop&q=80",
  "Web App": "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop&q=80",
  "Mobile App": "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop&q=80",
  HRMS: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop&q=80",
  CRM: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop&q=80",
  Automobile: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&h=600&fit=crop&q=80",
  Banking: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop&q=80",
  Travel: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&h=600&fit=crop&q=80",
};

export default function Services() {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const servicesData = [
    {
      title: "Website Development",
      desc: "High-converting marketing sites and landing pages built for speed, SEO, and lead generation.",
      bullets: ["Next.js / React", "Analytics + conversion tracking", "Performance budgets"],
      icon: "🌐",
      timeline: "2-4 weeks",
      targetAudience: ["Startups launching MVPs", "SMEs needing web presence", "Agencies with client projects", "Personal brands & portfolios"],
      deliverables: ["Responsive design", "SEO optimization", "Contact forms", "Analytics setup", "Performance testing", "Documentation"]
    },
    {
      title: "Web Application Development",
      desc: "Enterprise-grade portals and dashboards with real-time data, secure authentication, and role-based access.",
      bullets: ["React + Spring Boot", "JWT / OAuth", "API integrations"],
      icon: "💻",
      timeline: "6-12 weeks",
      targetAudience: ["SaaS companies", "Enterprise teams", "Internal tools", "B2B platforms"],
      deliverables: ["User authentication", "Database design", "Admin dashboard", "API documentation", "Security audit", "Performance monitoring"]
    },
    {
      title: "Banking Software & Web Apps",
      desc: "Secure banking websites, customer portals, mobile apps, and internal workflows with compliance-ready controls.",
      bullets: ["Web + mobile experiences", "Audit-ready logging", "Core banking integrations"],
      icon: "🏦",
      timeline: "8-16 weeks",
      targetAudience: ["Banks & NBFCs", "Fintech teams", "Loan servicing ops", "Compliance departments"],
      deliverables: ["KYC workflows", "Account dashboards", "Transaction reporting", "Alerts & notifications", "Compliance reports", "Integration testing"]
    },
    {
      title: "Mobile App Development",
      desc: "Cross-platform iOS & Android apps with native performance, offline mode, and push notifications.",
      bullets: ["React Native / Flutter", "Cloud sync", "Store readiness"],
      icon: "📱",
      timeline: "8-16 weeks",
      targetAudience: ["Consumer apps", "Enterprise mobility", "E-commerce businesses", "Service providers"],
      deliverables: ["iOS + Android apps", "Backend APIs", "Push notifications", "Offline mode", "Store submission", "Beta testing"]
    },
    {
      title: "CRM & Business Platforms",
      desc: "Lead management and automation with clear pipelines, analytics, and smart follow-ups.",
      bullets: ["Lead automation", "WhatsApp API", "Revenue dashboards"],
      icon: "📊",
      timeline: "8-14 weeks",
      targetAudience: ["Sales teams", "Marketing agencies", "Real estate firms", "Educational institutions"],
      deliverables: ["Lead capture & routing", "Pipeline management", "Email automation", "WhatsApp integration", "Analytics dashboard", "Reports"]
    },
    {
      title: "HRMS Software",
      desc: "Complete HR suite with attendance, payroll, leave management, and compliance-ready reporting.",
      bullets: ["Biometric integration", "Payroll automation", "Audit-ready"],
      icon: "👥",
      timeline: "10-16 weeks",
      targetAudience: ["SMEs & enterprises", "Manufacturing units", "Healthcare facilities", "Educational institutions"],
      deliverables: ["Employee database", "Attendance tracking", "Leave management", "Payroll processing", "Performance reviews", "Compliance reports"]
    },
    {
      title: "Travel & Transportation Management",
      desc: "Complete booking platforms for travel agencies managing cars, buses, taxis, cabs, and fleet operations with real-time tracking and customer management.",
      bullets: ["Vehicle booking system", "Route management", "GPS tracking"],
      icon: "🚕",
      timeline: "8-14 weeks",
      targetAudience: ["Travel agencies", "Cab operators", "Bus companies", "Car rental services"],
      deliverables: ["Booking portal", "Fleet management", "Driver app", "Customer app", "Payment gateway", "Route optimization", "Analytics dashboard"]
    },
    {
      title: "UI/UX Design",
      desc: "User research, wireframes, and design systems that reduce friction and improve conversion quality.",
      bullets: ["Figma prototypes", "User testing", "Design systems"],
      icon: "🎨",
      timeline: "2-6 weeks",
      targetAudience: ["Product teams", "Startups refining UX", "Rebranding projects", "Design audits"],
      deliverables: ["User research", "Wireframes", "High-fidelity mockups", "Interactive prototypes", "Design system", "Style guide"]
    },
    {
      title: "Cloud & DevOps",
      desc: "Deployment pipelines, monitoring, and performance tuning to keep releases fast and reliable.",
      bullets: ["CI/CD pipelines", "Observability", "Infrastructure tuning"],
      icon: "☁️",
      timeline: "2-8 weeks",
      targetAudience: ["Scaling startups", "DevOps teams", "Cloud migrations", "Performance optimization"],
      deliverables: ["CI/CD setup", "Cloud deployment", "Monitoring tools", "Security hardening", "Backup strategy", "Documentation"]
    },
    {
      title: "Final‑Year Student Projects",
      desc: "College project development in your required tech stack with documentation, PPT, and deployment support.",
      bullets: ["Stack‑matched builds", "Report + PPT templates", "Demo & submission support"],
      icon: "🎓",
      timeline: "5-15 days",
      targetAudience: ["Computer science students", "Final-year projects", "Academic submissions", "Technical demonstrations"],
      deliverables: ["Source code", "Project report", "PPT presentation", "ER diagrams", "Setup guide", "Demo support"]
    },
    {
      title: "Maintenance & Support",
      desc: "Proactive monitoring, security patches, performance optimization, and reliable release cycles.",
      bullets: ["Fast response SLAs", "Monthly health reports", "Zero-downtime updates"],
      icon: "🔧",
      timeline: "Ongoing",
      targetAudience: ["Production apps", "Growing platforms", "Enterprise systems", "Critical services"],
      deliverables: ["24/7 monitoring", "Bug fixes", "Security updates", "Performance tuning", "Monthly reports", "Feature updates"]
    },
  ];

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  return (
    <div className="space-y-12">
      <ServiceModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        service={selectedService} 
      />
      
      <TechHero
        kicker="Product engineering"
        title="Full-stack delivery with a premium UI finish"
        subtitle="From MVP to enterprise, we craft software that looks premium, performs fast, and scales safely."
        ctaText="Get a proposal"
      />
      <section className="animate-fade-in-up">
        <div className="grid items-center gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="order-2 lg:order-1">
            <p className="home-tagline">
              Services by <span className="brand-highlight">ZS NEXATECH</span>
            </p>
            <div className="page-title">
              <h1 className="mt-3 text-4xl font-bold text-white">
                Full-stack development that accelerates your time-to-market.
              </h1>
            </div>
            <p className="mt-4 max-w-2xl text-gray-100">
              <strong>Rapid delivery.</strong> <strong>Quality code.</strong> <strong>Modern stack.</strong> From concept to production with measurable outcomes.
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Website" },
                { label: "Web App" },
                { label: "Mobile App" },
                { label: "HRMS" },
                { label: "CRM" },
                { label: "Automobile" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="card animate-card-flip-3d overflow-hidden rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg shadow-sm transition-all duration-500 hover:scale-105 hover:-translate-y-1 hover:shadow-2xl hover:bg-white/20 group"
                >
                  <div className="relative flex h-32 items-center justify-center overflow-hidden">
                    <img 
                      src={serviceImages[item.label]} 
                      alt={item.label}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-125 transition-all duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/50 via-rose-500/40 to-purple-600/50 group-hover:from-amber-400/60 group-hover:via-rose-400/50 group-hover:to-purple-500/60 transition-all duration-500"></div>
                    <span className="relative text-5xl drop-shadow-2xl z-10 transform group-hover:scale-125 group-hover:rotate-12 transition-all duration-500">{serviceIcons[item.label]}</span>
                  </div>
                  <p className="px-3 py-3 text-center text-sm font-bold text-white tracking-wide">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="card-grid grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {servicesData.map((service) => (
          <div
            key={service.title}
            onClick={() => handleServiceClick(service)}
            className="card animate-card-flip-3d rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 shadow-sm hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden cursor-pointer"
          >
            <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-rose-600/0 group-hover:from-amber-500/10 group-hover:to-rose-600/10 transition-all duration-500"></div>
            <div className="relative z-10">
              <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">{service.icon}</div>
              <h3 className="text-lg font-semibold text-white">{service.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{service.desc}</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-100">
                {service.bullets.map((item) => (
                  <li key={item} className="flex items-start">
                    <span className="text-amber-400 mr-2 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-sm font-semibold text-amber-300 flex items-center gap-2 group-hover:gap-3 transition-all">
                <span>Learn more</span>
                <span className="transform group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">✨ Solution packages</h2>
          <p className="mt-2 text-gray-100">
            Choose the package that fits your goals. All include QA, performance checks, and launch support.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: "Starter",
              subtitle: "Landing Pages",
              timeline: "1-2 weeks",
              features: ["Responsive design", "SEO optimization", "Contact forms", "Fast loading", "Mobile-first"],
              tag: "Quick launch",
              icon: "🚀",
            },
            {
              title: "Growth",
              subtitle: "Business Websites",
              timeline: "3-4 weeks",
              features: ["Multi-page site", "CMS integration", "Analytics setup", "Conversion tracking", "Email integration"],
              tag: "Most popular",
              icon: "⭐",
            },
            {
              title: "Professional",
              subtitle: "Web Applications",
              timeline: "6-12 weeks",
              features: ["Custom features", "User authentication", "Database design", "Admin dashboard", "API integration"],
              tag: "Scalable",
              icon: "📈",
            },
            {
              title: "Enterprise",
              subtitle: "Mobile Apps",
              timeline: "8-16 weeks",
              features: ["iOS + Android", "Backend APIs", "Push notifications", "Offline mode", "Store launch"],
              tag: "Full-featured",
              icon: "💎",
            },
          ].map((pkg) => (
            <div
              key={pkg.title}
              className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300 relative pt-8 pb-6 px-6 group animate-card-flip-3d overflow-hidden"
            >
              <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/5 to-rose-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              {pkg.tag && (
                <span className="absolute top-2 left-1/2 -translate-x-1/2 bg-linear-to-r from-red-500 to-pink-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg z-10 animate-pulse">
                  {pkg.tag}
                </span>
              )}
              <div className="relative z-10">
                <div className="text-4xl mb-3 transform group-hover:scale-110 transition-transform duration-300">{pkg.icon}</div>
                <h3 className="text-xl font-bold text-white">{pkg.title}</h3>
                <p className="text-sm text-gray-100">{pkg.subtitle}</p>
                <div className="mt-3 py-3 border-y border-white/20">
                  <p className="text-sm text-gray-100">⏱️ Timeline: <strong>{pkg.timeline}</strong></p>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-gray-100">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-center">
                      <span className="text-green-400 mr-2">✓</span> {feature}
                    </li>
                  ))}
                </ul>
                <a 
                  href="/contact" 
                  className="mt-4 block w-full text-center btn-gold text-sm py-2 transform group-hover:scale-105 transition-transform duration-300"
                >
                  Get Quote
                </a>
              </div>
            </div>
          ))}
        </div>
        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 text-center">
          <p className="text-sm text-white/90">
            <strong>💡 Custom requirements?</strong> Need banking platforms, HRMS, CRM, E‑commerce, Travel & Transportation systems, or specific integrations?
            <a href="/contact" className="link-gold ml-1">Let’s discuss your project</a>
          </p>
          <p className="mt-2 text-xs text-white/70">
            💰 <strong>Affordable rates for startups</strong> • Flexible payment plans available
          </p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300">
          <h2 className="text-2xl font-bold text-white">🏢 Industries we serve</h2>
          <p className="mt-2 text-sm text-gray-100">
            We tailor features and workflows based on your industry requirements.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { name: "Education & Coaching", icon: "🎓" },
              { name: "Restaurants & Cafes", icon: "🍽️" },
              { name: "Salons & Wellness", icon: "💆" },
              { name: "Clinics & Hospitals", icon: "🏥" },
              { name: "Retail & E-commerce", icon: "🛒" },
              { name: "Banking & Financial Services", icon: "🏦" },
              { name: "Real Estate", icon: "🏢" },
              { name: "Automobile & Dealerships", icon: "🚗" },
              { name: "Travel & Transportation", icon: "🚕" },
              { name: "Professional Services", icon: "💼" },
              { name: "Startups & SMEs", icon: "🚀" },
            ].map((item) => (
              <div
                key={item.name}
                className="card rounded-xl border border-white/30 bg-white/10 backdrop-blur-md px-4 py-3 text-sm font-medium text-white/90 hover:bg-white/20 hover:border-white/40 hover:scale-105 hover:-translate-y-1 transition-all duration-300 group flex items-center gap-2"
              >
                <span className="text-lg transform group-hover:scale-125 transition-transform duration-300">{item.icon}</span>
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:bg-white/20 transition-all duration-300">
          <h2 className="text-2xl font-bold text-white">🤝 Flexible engagement models</h2>
          <p className="mt-2 text-sm text-gray-100">
            Choose the pricing model that fits your budget and timeline.
          </p>
          <ul className="mt-4 space-y-3 text-sm text-gray-100">
            <li>
              <span className="font-semibold text-white">💰 Fixed Price:</span> Best for
              defined scope projects. Clear deliverables, milestone-based payments.
            </li>
            <li>
              <span className="font-semibold text-white">👥 Dedicated Team:</span> Your extended development
              team working exclusively on your project.
            </li>
            <li>
              <span className="font-semibold text-white">⏱️ Hourly / Sprint Based:</span>
              Flexible development for evolving requirements. Perfect for iterative projects.
            </li>
          </ul>
          <p className="mt-4 text-xs text-white/70 italic">💡 Contact us for customized quotes based on your requirements.</p>
        </div>
      </section>

      <section className="premium-panel glass-panel animated-border">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div>
            <span className="section-kicker holo-gradient text-slate-900">Delivery standards</span>
            <h2 className="mt-4 text-3xl font-bold text-white">Engineering discipline baked into every sprint</h2>
            <p className="mt-3 text-gray-100">
              We build with strong QA, security checks, and performance budgets so your product launches with confidence.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {[
                "QA + regression testing",
                "Performance budgets",
                "Security checks",
                "Release checklists",
              ].map((item) => (
                <span key={item} className="icon-pill">✅ {item}</span>
              ))}
            </div>
          </div>
          <div className="grid gap-4">
            {[
              { title: "Design QA", desc: "Pixel‑perfect UI with design system governance." },
              { title: "Code QA", desc: "Reviews, linting, and automated testing." },
              { title: "Launch QA", desc: "Performance, SEO, and monitoring checks." },
            ].map((item) => (
              <div key={item.title} className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-5">
                <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-gray-100">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <h2 className="text-3xl font-bold text-white">✨ Complete delivery package</h2>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {["✅ Requirements analysis + technical roadmap", "✅ UI/UX design + interactive prototypes", "✅ Clean, documented code + API documentation", "✅ Testing (unit + integration + UAT)", "✅ Cloud deployment + CI/CD setup", "✅ User training + admin documentation"].map(
            (item) => (
              <div
                key={item}
                className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-md px-4 py-4 text-sm text-white/90 hover:bg-white/20 hover:scale-105 transition-all duration-300"
              >
                {item}
              </div>
            )
          )}
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="/contact" className="btn-gold">Request a quote</a>
          <a className="link-gold" href="/contact">
            Talk to us
          </a>
        </div>
      </section>
    </div>
  );
}
